import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import {
	Link2,
	Play,
	Loader2,
	Globe,
	AlertCircle,
	CheckCircle,
	XCircle,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/utils/cn";
import { testURLDetection } from "@/services/simulation";
import { useToast } from "@/components/ui/use-toast";

const browserOptions = [
	"Google Chrome",
	"Firefox",
	"Safari",
	"Microsoft Edge",
	"Brave",
	"Arc",
	"Opera",
	"Vivaldi",
];

const exampleTitles = [
	"GitHub - anthropics/claude-code - Google Chrome",
	"Stack Overflow - How to center a div - Firefox",
	"YouTube - Learn TypeScript Tutorial - Brave",
	"Netflix - Stranger Things - Google Chrome",
	"Amazon.com: Shopping - Google Chrome",
	"Figma - Design Dashboard - Arc",
	"ChatGPT - Open AI - Safari",
	"Reddit - r/programming - Microsoft Edge",
	"Google Docs - Project Plan - Google Chrome",
	"Slack - Work Channel - Safari",
];

const productivityColors: Record<string, { bg: string; text: string }> = {
	productive: { bg: "bg-green-100", text: "text-green-700" },
	neutral: { bg: "bg-gray-100", text: "text-gray-700" },
	unproductive: { bg: "bg-red-100", text: "text-red-700" },
};

export function URLDetector() {
	const { toast } = useToast();
	const [formData, setFormData] = useState({
		windowTitle: "GitHub - anthropics/claude-code - Google Chrome",
		appName: "Google Chrome",
	});

	const urlMutation = useMutation({
		mutationFn: testURLDetection,
		onError: (error: any) => {
			toast({
				title: "Error",
				description: error.message || "Failed to detect URL",
				variant: "destructive",
			});
		},
	});

	const handleSubmit = () => {
		urlMutation.mutate(formData);
	};

	const result = urlMutation.data;

	return (
		<div className="space-y-6">
			{/* Header */}
			<div className="flex items-center justify-between">
				<div>
					<h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
						<Link2 className="w-6 h-6" />
						URL Detector
					</h1>
					<p className="text-gray-500 mt-1">
						Test URL inference from browser window titles
					</p>
				</div>
			</div>

			<div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
				{/* Input Form */}
				<Card>
					<CardHeader>
						<CardTitle>Window Information</CardTitle>
					</CardHeader>
					<CardContent className="space-y-6">
						{/* Browser Selection */}
						<div>
							<Label>Browser</Label>
							<div className="flex flex-wrap gap-2 mt-2">
								{browserOptions.map((browser) => (
									<Button
										key={browser}
										size="sm"
										variant={formData.appName === browser ? "default" : "outline"}
										onClick={() => setFormData({ ...formData, appName: browser })}
									>
										{browser}
									</Button>
								))}
							</div>
						</div>

						{/* Window Title */}
						<div>
							<Label>Window Title</Label>
							<Input
								className="mt-2"
								placeholder="Enter window title..."
								value={formData.windowTitle}
								onChange={(e) =>
									setFormData({ ...formData, windowTitle: e.target.value })
								}
							/>
						</div>

						{/* Example Titles */}
						<div>
							<Label className="text-gray-600">Quick Examples</Label>
							<div className="mt-2 space-y-1 max-h-48 overflow-y-auto">
								{exampleTitles.map((title) => (
									<button
										key={title}
										type="button"
										onClick={() => {
											setFormData({
												...formData,
												windowTitle: title,
												appName: title.split(" - ").pop() || "Google Chrome",
											});
										}}
										className="w-full text-left text-sm p-2 hover:bg-gray-50 rounded border border-transparent hover:border-gray-200 transition-colors truncate"
									>
										{title}
									</button>
								))}
							</div>
						</div>

						{/* Detect Button */}
						<Button
							className="w-full"
							size="lg"
							onClick={handleSubmit}
							disabled={urlMutation.isPending}
						>
							{urlMutation.isPending ? (
								<Loader2 className="w-4 h-4 mr-2 animate-spin" />
							) : (
								<Play className="w-4 h-4 mr-2" />
							)}
							Detect URL
						</Button>
					</CardContent>
				</Card>

				{/* Results */}
				<Card>
					<CardHeader>
						<CardTitle>Detection Result</CardTitle>
					</CardHeader>
					<CardContent>
						{result ? (
							<div className="space-y-6">
								{/* Detection Status */}
								<div className={cn(
									"p-6 rounded-lg text-center",
									result.detected ? "bg-green-50" : "bg-gray-50"
								)}>
									{result.detected ? (
										<>
											<CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-500" />
											<p className="font-medium text-green-700">URL Detected</p>
										</>
									) : (
										<>
											<AlertCircle className="w-12 h-12 mx-auto mb-3 text-gray-400" />
											<p className="font-medium text-gray-600">No URL Detected</p>
											<p className="text-sm text-gray-500 mt-1">
												Could not infer URL from window title
											</p>
										</>
									)}
								</div>

								{result.detected && (
									<>
										{/* URL Details */}
										<div className="space-y-3">
											<div className="p-3 bg-gray-50 rounded-lg">
												<p className="text-xs text-gray-500 uppercase font-medium mb-1">
													Detected URL
												</p>
												<p className="font-mono text-sm break-all">
													{result.url}
												</p>
											</div>
											<div className="p-3 bg-gray-50 rounded-lg">
												<p className="text-xs text-gray-500 uppercase font-medium mb-1">
													Domain
												</p>
												<div className="flex items-center gap-2">
													<Globe className="w-4 h-4 text-gray-400" />
													<p className="font-medium">{result.domain}</p>
												</div>
											</div>
											<div className="grid grid-cols-2 gap-3">
												<div className="p-3 bg-gray-50 rounded-lg">
													<p className="text-xs text-gray-500 uppercase font-medium mb-1">
														Confidence
													</p>
													<p className="font-bold text-lg">
														{Math.round(result.confidence * 100)}%
													</p>
												</div>
												<div className="p-3 bg-gray-50 rounded-lg">
													<p className="text-xs text-gray-500 uppercase font-medium mb-1">
														Source
													</p>
													<Badge variant="outline" className="capitalize">
														{result.source?.replace(/_/g, " ")}
													</Badge>
												</div>
											</div>
										</div>

										{/* Classification */}
										<div className="border-t pt-4">
											<h4 className="font-medium text-gray-700 mb-3">
												Classification
											</h4>
											<div className={cn(
												"p-4 rounded-lg flex items-center justify-between",
												productivityColors[result.productivityLevel]?.bg
											)}>
												<div className="flex items-center gap-3">
													{result.productivityLevel === "productive" && (
														<CheckCircle className="w-6 h-6 text-green-600" />
													)}
													{result.productivityLevel === "neutral" && (
														<AlertCircle className="w-6 h-6 text-gray-600" />
													)}
													{result.productivityLevel === "unproductive" && (
														<XCircle className="w-6 h-6 text-red-600" />
													)}
													<div>
														<p className={cn(
															"font-medium capitalize",
															productivityColors[result.productivityLevel]?.text
														)}>
															{result.productivityLevel}
														</p>
														{result.classification && (
															<p className="text-sm text-gray-600">
																Category: {result.classification.category}
															</p>
														)}
													</div>
												</div>
												{result.classification && (
													<Badge variant="outline">
														{Math.round(result.classification.confidence * 100)}% confidence
													</Badge>
												)}
											</div>
										</div>
									</>
								)}
							</div>
						) : (
							<div className="text-center py-12 text-gray-500">
								<Link2 className="w-12 h-12 mx-auto mb-4 opacity-50" />
								<p>Enter a window title and click Detect to see results</p>
							</div>
						)}
					</CardContent>
				</Card>
			</div>
		</div>
	);
}
